import { Appointment } from '../../../models/appointment/Appointment'
import { ProgramNames } from '../../../models/programs/Programs'
import { getProgramNameFromId } from '../../programConfigs/programHelpers'
import { LTSessionTypes } from '../constants'

export type DbtTeensSessionAttendeeAppointment = Pick<Appointment, 'programId' | 'sessionNumber' | 'sessionType'>
export type DbtTeensAttendeeRole = 'teen' | 'caregiver'

const getAttendeeRoles = ({
  sessionNumber,
  sessionType,
}: Pick<DbtTeensSessionAttendeeAppointment, 'sessionNumber' | 'sessionType'>): DbtTeensAttendeeRole[] | undefined => {
  switch (sessionType) {
    case LTSessionTypes.DBT_TEENS_INTAKE_SESSION:
    case LTSessionTypes.DBT_TEENS_FAMILY_SESSION:
    case LTSessionTypes.DBT_TEENS_MEDICATION_INITIAL_SESSION:
    case LTSessionTypes.DBT_TEENS_MEDICATION_FOLLOWUP_SESSION:
      return ['teen', 'caregiver']
    case LTSessionTypes.DBT_TEENS_FOLLOWUP_SESSION:
      return ['teen']
    case LTSessionTypes.DBT_TEENS_CAREGIVER_SESSION:
      return ['caregiver']
    case LTSessionTypes.DBT_TEENS_GROUP_SESSION:
      return undefined
    default:
      return sessionNumber === 1 ? ['teen', 'caregiver'] : ['teen']
  }
}

/**
 * Who attends a DBT Teens session (teen, caregiver, or both), from its session type and number.
 * Undefined for sessions outside the DBT Teens program and for group sessions.
 */
export const getDbtTeensSessionAttendeeRoles = (
  appointment: DbtTeensSessionAttendeeAppointment,
): DbtTeensAttendeeRole[] | undefined => {
  if (getProgramNameFromId(appointment.programId) !== ProgramNames.DialecticalBehaviorTherapyTeens) {
    return undefined
  }
  return getAttendeeRoles(appointment)
}

/**
 * Returns the expected attendee names for a member-facing DBT Teens session label.
 */
export const getDbtTeensSessionAttendeeNames = ({
  appointment,
  teenFirstName,
  caregiverFirstName,
}: {
  appointment: DbtTeensSessionAttendeeAppointment
  teenFirstName?: string | null
  caregiverFirstName?: string | null
}): string[] | undefined => {
  const attendeeRoles = getDbtTeensSessionAttendeeRoles(appointment)
  if (!attendeeRoles) {
    return undefined
  }

  const namesByRole: Record<DbtTeensAttendeeRole, string | undefined> = {
    teen: teenFirstName?.trim() || undefined,
    caregiver: caregiverFirstName?.trim() || undefined,
  }
  const attendeeNames = attendeeRoles.map((role) => namesByRole[role])

  return attendeeNames.some((name) => !name) ? undefined : (attendeeNames as string[])
}
